import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import "./ForgotPassword.css";
import "../Home/publicPages.css";
import "../Home/authPages.css";

const PORTAL_COPY = {
  trainee: {
    eyebrow: "Learner portal",
    title: "Reset Your Password",
    description:
      "We'll send an OTP to your email so you can securely set a new password and get back to learning.",
    points: [
      "Verify with a one-time OTP",
      "Create a new secure password",
      "Sign in and continue learning",
    ],
    signInPath: "/trainee-signin",
    pageClass: "trainee-auth-page",
  },
  trainer: {
    eyebrow: "Trainer portal",
    title: "Reset Your Password",
    description:
      "We'll send an OTP to your email so you can securely set a new password and return to your trainer dashboard.",
    points: [
      "Verify with a one-time OTP",
      "Create a new secure password",
      "Sign in and manage your courses",
    ],
    signInPath: "/trainer-signin",
    pageClass: "trainer-auth-page",
  },
  user: {
    eyebrow: "Employer portal",
    title: "Reset Your Password",
    description:
      "We'll send an OTP to your email so you can securely set a new password and return to your employer account.",
    points: [
      "Verify with a one-time OTP",
      "Create a new secure password",
      "Sign in and continue hiring",
    ],
    signInPath: "/employer-signin",
    pageClass: "employer-auth-page",
  },
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [error, setError] = useState("");
  const userType = location.state?.userType || "trainee";
  const portal = PORTAL_COPY[userType] || PORTAL_COPY.trainee;

  const handleRequestOtp = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email.");
      return;
    }

    try {
      const response = await AxiosInstance.post(
        "/companies/forgot-password/otp/",
        { email, user_type: userType }
      );

      setIsOtpSent(true);
      setError("");
      alert(response.data.message || "OTP sent to your email!");
    } catch (err) {
      const errorMsg =
        err.response?.data?.error || "Server error. Please try again.";
      setError(errorMsg);
      console.error("OTP request error:", err);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!otp || !newPassword) {
      setError("Please enter OTP and new password.");
      return;
    }

    try {
      const response = await AxiosInstance.post(
        "/companies/forgot-password/reset/",
        {
          email,
          otp,
          new_password: newPassword,
          user_type: userType,
        }
      );

      alert(response.data.message || "Password reset successfully!");
      navigate(portal.signInPath);
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to reset password.";
      setError(errorMsg);
      console.error("Password reset error:", err);
    }
  };

  return (
    <div className={`als-auth-page forgot-password-page ${portal.pageClass}`}>
      <div className="als-auth-shell">
        <aside className="als-auth-brand">
          <div className="als-auth-brand-inner">
            <p className="als-auth-eyebrow">{portal.eyebrow}</p>
            <h1>{portal.title}</h1>
            <p>{portal.description}</p>
            <ul className="als-auth-points">
              {portal.points.map((point) => (
                <li key={point}>
                  <span>✓</span> {point}
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="als-auth-panel">
          <div className="auth-forms-container">
            <div className="signin-container">
              <h3>{isOtpSent ? "Reset Password" : "Forgot Password"}</h3>
              <p className="als-auth-lead">
                {isOtpSent
                  ? "Enter the OTP sent to your email and set a new password."
                  : "We'll send an OTP to your email so you can set a new password."}
              </p>

              {!isOtpSent ? (
                <form onSubmit={handleRequestOtp}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="form-control"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      required
                    />
                  </div>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  <button type="submit" className="btn1">
                    Request OTP
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword}>
                  <div className="mb-3">
                    <label htmlFor="otp" className="form-label">
                      OTP
                    </label>
                    <input
                      type="text"
                      id="otp"
                      className="form-control"
                      placeholder="Enter the OTP"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        setError("");
                      }}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      className="form-control"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setError("");
                      }}
                      required
                    />
                  </div>

                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  <button type="submit" className="btn1">
                    Reset Password
                  </button>
                </form>
              )}

              <div className="als-auth-footer-links">
                <small>
                  Remember your password?{" "}
                  <Link to={portal.signInPath} className="green-hover-link1">
                    Sign In
                  </Link>
                </small>
                <small>
                  <Link to="/" className="green-hover-link1">
                    Home
                  </Link>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
